import { z } from 'zod';
import { defineSyncedEntity } from './shapes';
import { panFreeString, panFreeStringNull } from './pan';
import {
  zUuid,
  zBusinessDate,
  zTimestamptz,
  zCents,
  zCentsNull,
  zCurrency,
  zStringArray,
  zUuidArray,
  zJson,
} from './primitives';
import * as E from './enums';

type SyncedShapes = ReturnType<typeof defineSyncedEntity>;

function refineSynced<T extends SyncedShapes>(
  entity: T,
  check: (data: z.infer<T['body']>, ctx: z.RefinementCtx) => void,
) {
  return {
    body: entity.body.superRefine(check),
    localEntity: entity.localEntity.superRefine(check),
    remoteRow: entity.remoteRow.superRefine(check),
    remoteInsertPayload: entity.remoteInsertPayload.superRefine(check),
  };
}

const zPositiveCents = zCents.refine((n) => n > 0n, {
  message: 'amount must be > 0',
});

const zNonNegativeCents = zCents.refine((n) => n >= 0n, {
  message: 'amount must be >= 0',
});

const displaySuffix = z.string().regex(/^[0-9]{3,8}$/);

const reviewSeverity = z.enum(['info', 'warn', 'block']);

// ---------------------------------------------------------------------------
// evidence_sources
// ---------------------------------------------------------------------------

const evidenceSourcesFields = {
  id: zUuid,
  card_id: zUuid.nullable(),
  kind: E.sourceType,
  title: panFreeString,
  source_date: zBusinessDate.nullable(),
  issuer: panFreeStringNull,
  file_name: panFreeStringNull,
  file_hash: panFreeStringNull,
  storage_path: panFreeStringNull,
  official_url: panFreeStringNull,
  account_specific: z.boolean(),
  document_version: panFreeStringNull,
  retrieved_at: zBusinessDate,
  supersedes_id: zUuid.nullable(),
  rep_name: panFreeStringNull,
  call_reference: panFreeStringNull,
  confirmation_expires_at: zBusinessDate.nullable(),
  notes: panFreeStringNull,
};
export const evidenceSources = defineSyncedEntity(evidenceSourcesFields);
export type EvidenceSourceLocal = z.infer<typeof evidenceSources.localEntity>;
export type EvidenceSourceRemote = z.infer<typeof evidenceSources.remoteRow>;
export type EvidenceSourceInsert = z.infer<typeof evidenceSources.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// cards
// ---------------------------------------------------------------------------

const cardsFields = {
  id: zUuid,
  issuer: panFreeString,
  product_name: panFreeStringNull,
  product_name_confidence: E.confidenceTier,
  nickname: panFreeString,
  display_suffix: displaySuffix,
  suffix_type: E.suffixKind,
  network: E.cardNetwork,
  network_tier: panFreeStringNull,
  owner: E.ownerType,
  open_date: zBusinessDate.nullable(),
  status: E.accountStatus,
  spend_policy: E.spendPolicy,
  spend_policy_reason: panFreeStringNull,
  disposition: E.portfolioDisposition,
  disposition_reason: panFreeStringNull,
  annual_fee_cents: zCentsNull,
  monthly_fee_cents: zCentsNull,
  fee_notes: panFreeStringNull,
  foreign_txn_fee_bps: z.number().int().nullable(),
  cash_advance_notes: panFreeStringNull,
  purchase_apr_bps: z.number().int().nullable(),
  grace_exists: z.boolean().nullable(),
  grace_restore: E.graceRestoreRule,
  grace_policy_confidence: E.confidenceTier,
  grace_policy_source_id: zUuid.nullable(),
  official_product_url: panFreeStringNull,
  closed_loop_merchant: panFreeStringNull,
  statement_day_of_month: z.number().int().nullable(),
  due_days_after_close: z.number().int().nullable(),
  notes: panFreeStringNull,
};

function refineCard(
  data: {
    grace_restore: z.infer<typeof E.graceRestoreRule>;
    grace_policy_source_id: string | null;
    network: z.infer<typeof E.cardNetwork>;
    closed_loop_merchant: string | null;
  },
  ctx: z.RefinementCtx,
) {
  if (data.grace_restore !== 'unknown' && data.grace_policy_source_id == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'grace_policy_needs_source',
      path: ['grace_policy_source_id'],
    });
  }
  if (data.network === 'closed_loop' && data.closed_loop_merchant == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'closed_loop_needs_merchant',
      path: ['closed_loop_merchant'],
    });
  }
}

export const cards = refineSynced(defineSyncedEntity(cardsFields), refineCard);
export type CardLocal = z.infer<typeof cards.localEntity>;
export type CardRemote = z.infer<typeof cards.remoteRow>;
export type CardInsert = z.infer<typeof cards.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// card_financial_observations
// ---------------------------------------------------------------------------

const cardFinancialObservationsFields = {
  id: zUuid,
  card_id: zUuid,
  observed_at: zTimestamptz,
  source: E.observationSource,
  evidence_source_id: zUuid,
  current_balance_cents: zCentsNull,
  pending_balance_cents: zCentsNull,
  statement_balance_cents: zCentsNull,
  minimum_payment_cents: zCentsNull,
  available_credit_cents: zCentsNull,
  credit_limit_cents: zCentsNull,
  cash_advance_limit_cents: zCentsNull,
  payment_due_date: zBusinessDate.nullable(),
  statement_closing_date: zBusinessDate.nullable(),
  next_closing_date: zBusinessDate.nullable(),
  purchase_apr_bps: z.number().int().nullable(),
  promotional_apr_bps: z.number().int().nullable(),
  promotional_apr_end: zBusinessDate.nullable(),
  account_restricted: z.boolean().nullable(),
  last_payment_date: zBusinessDate.nullable(),
  last_payment_amount_cents: zCentsNull,
  reflects_txn_ids: zUuidArray,
  same_day_ambiguous: z.boolean(),
  retracts_fields: zStringArray,
  retraction_reason: panFreeStringNull,
};
export const cardFinancialObservations = defineSyncedEntity(cardFinancialObservationsFields);
export type CardFinancialObservationLocal = z.infer<typeof cardFinancialObservations.localEntity>;
export type CardFinancialObservationRemote = z.infer<typeof cardFinancialObservations.remoteRow>;
export type CardFinancialObservationInsert = z.infer<
  typeof cardFinancialObservations.remoteInsertPayload
>;

// ---------------------------------------------------------------------------
// card_autopay_observations
// ---------------------------------------------------------------------------

const cardAutopayObservationsFields = {
  id: zUuid,
  card_id: zUuid,
  observed_at: zTimestamptz,
  source: E.observationSource,
  evidence_source_id: zUuid,
  enabled: z.boolean().nullable(),
  autopay_kind: E.autopayType,
  fixed_amount_cents: zCentsNull,
  scheduled_day: z.number().int().nullable(),
  funding_account_label: panFreeStringNull,
};
export const cardAutopayObservations = defineSyncedEntity(cardAutopayObservationsFields);
export type CardAutopayObservationLocal = z.infer<typeof cardAutopayObservations.localEntity>;
export type CardAutopayObservationRemote = z.infer<typeof cardAutopayObservations.remoteRow>;
export type CardAutopayObservationInsert = z.infer<
  typeof cardAutopayObservations.remoteInsertPayload
>;

// ---------------------------------------------------------------------------
// grace_confirmations
// ---------------------------------------------------------------------------

const graceConfirmationsFields = {
  id: zUuid,
  card_id: zUuid,
  confirmed_status: E.graceStatus,
  reason: panFreeString,
  confirmed_at: zTimestamptz,
  expires_at: zBusinessDate,
  issuer_stated_expiry: zBusinessDate.nullable(),
  evidence_source_id: zUuid,
};
export const graceConfirmations = defineSyncedEntity(graceConfirmationsFields);
export type GraceConfirmationLocal = z.infer<typeof graceConfirmations.localEntity>;
export type GraceConfirmationRemote = z.infer<typeof graceConfirmations.remoteRow>;
export type GraceConfirmationInsert = z.infer<typeof graceConfirmations.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// field_assertions
// ---------------------------------------------------------------------------

const fieldAssertionsFields = {
  id: zUuid,
  entity_type: z.literal('card'),
  entity_id: zUuid,
  field_name: panFreeString,
  asserted_value: zJson,
  evidence_source_id: zUuid,
  confidence: E.confidenceTier,
  status: E.verificationStatus,
  verified_at: zBusinessDate,
  superseded_at: zBusinessDate.nullable(),
  superseded_by_id: zUuid.nullable(),
  note: panFreeStringNull,
};
export const fieldAssertions = defineSyncedEntity(fieldAssertionsFields);
export type FieldAssertionLocal = z.infer<typeof fieldAssertions.localEntity>;
export type FieldAssertionRemote = z.infer<typeof fieldAssertions.remoteRow>;
export type FieldAssertionInsert = z.infer<typeof fieldAssertions.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// reward_rules
// ---------------------------------------------------------------------------

const rewardRulesFields = {
  id: zUuid,
  card_id: zUuid,
  scope: E.evidenceScope,
  currency: E.rewardCurrency,
  reward_rate_bps: z.number().int().nullable(),
  milli_points_per_dollar: z.number().int().nullable(),
  milli_cents_per_point: z.number().int().nullable(),
  cash_value_basis: panFreeStringNull,
  category: E.spendCategory.nullable(),
  merchant_match: E.merchantScope,
  merchant_names: z.array(panFreeString).nullable(),
  channel: E.txnChannel,
  geography: E.geoScope,
  requires_activation: z.boolean(),
  activated: z.boolean().nullable(),
  activation_deadline: zBusinessDate.nullable(),
  requires_membership: z.boolean(),
  membership_type: panFreeStringNull,
  membership_active: z.boolean().nullable(),
  requires_travel_portal: z.boolean(),
  spend_cap_cents: zCentsNull,
  cap_window: E.capPeriod,
  cap_anchor_date: zBusinessDate.nullable(),
  post_cap_rate_bps: z.number().int().nullable(),
  valid_from: zBusinessDate.nullable(),
  valid_until: zBusinessDate.nullable(),
  excluded_txn_types: zStringArray,
  mutually_exclusive_with: zUuidArray,
  stacking: E.stackingRule,
  priority: z.number().int(),
  confidence: E.confidenceTier,
  last_verified_at: zBusinessDate.nullable(),
  evidence_source_id: zUuid,
};
export const rewardRules = defineSyncedEntity(rewardRulesFields);
export type RewardRuleLocal = z.infer<typeof rewardRules.localEntity>;
export type RewardRuleRemote = z.infer<typeof rewardRules.remoteRow>;
export type RewardRuleInsert = z.infer<typeof rewardRules.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// benefits
// ---------------------------------------------------------------------------

const benefitsFields = {
  id: zUuid,
  card_id: zUuid,
  scope: E.evidenceScope,
  kind: E.benefitType,
  confirmed_absent: z.boolean(),
  coverage_amount_cents: zCentsNull,
  per_claim_limit_cents: zCentsNull,
  annual_limit_cents: zCentsNull,
  deductible_cents: zCentsNull,
  coverage_period_days: z.number().int().nullable(),
  position: E.coveragePosition,
  eligibility: panFreeStringNull,
  exclusions: panFreeStringNull,
  claim_deadline_days: z.number().int().nullable(),
  requires_full_purchase_on_card: z.boolean().nullable(),
  requires_prior_bill_paid_on_card: z.boolean().nullable(),
  min_purchase_count: z.number().int().nullable(),
  max_claims_per_year: z.number().int().nullable(),
  max_claims_per_occurrence: z.number().int().nullable(),
  per_day_limit_cents: zCentsNull,
  max_covered_days: z.number().int().nullable(),
  max_rental_days: z.number().int().nullable(),
  limit_basis: E.limitBasis.nullable(),
  terms_jsonb: zJson,
  valid_from: zBusinessDate.nullable(),
  valid_until: zBusinessDate.nullable(),
  confidence: E.confidenceTier,
  last_verified_at: zBusinessDate.nullable(),
  evidence_source_id: zUuid,
};
export const benefits = defineSyncedEntity(benefitsFields);
export type BenefitLocal = z.infer<typeof benefits.localEntity>;
export type BenefitRemote = z.infer<typeof benefits.remoteRow>;
export type BenefitInsert = z.infer<typeof benefits.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// card_fee_schedules
// ---------------------------------------------------------------------------

const cardFeeSchedulesFields = {
  id: zUuid,
  card_id: zUuid,
  scope: E.evidenceScope,
  kind: E.feeKind,
  label: panFreeStringNull,
  amount_cents: zNonNegativeCents,
  frequency: E.feeFrequency,
  billed_as_monthly_installment: z.boolean(),
  annualized_cents: zCentsNull,
  effective_from: zBusinessDate.nullable(),
  effective_until: zBusinessDate.nullable(),
  is_optional: z.boolean(),
  is_cancelable: z.boolean().nullable(),
  cancellation_notes: panFreeStringNull,
  active: z.boolean(),
  next_charge_date: zBusinessDate.nullable(),
  confidence: E.confidenceTier,
  evidence_source_id: zUuid,
  last_verified_at: zBusinessDate.nullable(),
};
export const cardFeeSchedules = defineSyncedEntity(cardFeeSchedulesFields);
export type CardFeeScheduleLocal = z.infer<typeof cardFeeSchedules.localEntity>;
export type CardFeeScheduleRemote = z.infer<typeof cardFeeSchedules.remoteRow>;
export type CardFeeScheduleInsert = z.infer<typeof cardFeeSchedules.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// merchants
// ---------------------------------------------------------------------------

const merchantsFields = {
  id: zUuid,
  normalized_name: panFreeString,
  raw_names: z.array(panFreeString),
  default_category: E.spendCategory.nullable(),
  online_category: E.spendCategory.nullable(),
  in_store_category: E.spendCategory.nullable(),
  possible_mccs: zStringArray,
  category_confidence: E.confidenceTier,
  is_intermediary: z.boolean(),
  known_exceptions: panFreeStringNull,
  parent_merchant_id: zUuid.nullable(),
  last_verified_at: zBusinessDate.nullable(),
};
export const merchants = defineSyncedEntity(merchantsFields);
export type MerchantLocal = z.infer<typeof merchants.localEntity>;
export type MerchantRemote = z.infer<typeof merchants.remoteRow>;
export type MerchantInsert = z.infer<typeof merchants.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// offers
// ---------------------------------------------------------------------------

const offersFields = {
  id: zUuid,
  card_id: zUuid,
  merchant: panFreeString,
  merchant_id: zUuid.nullable(),
  kind: E.offerType,
  value_bps: z.number().int().nullable(),
  value_cents: zCentsNull,
  minimum_spend_cents: zCentsNull,
  maximum_reward_cents: zCentsNull,
  activation_required: z.boolean(),
  activated: z.boolean(),
  activated_at: zBusinessDate.nullable(),
  valid_from: zBusinessDate,
  valid_until: zBusinessDate,
  channel: E.txnChannel,
  one_time: z.boolean(),
  consumed_at: zBusinessDate.nullable(),
  terms: panFreeStringNull,
  scope: E.evidenceScope,
  confidence: E.confidenceTier,
  evidence_source_id: zUuid,
  last_verified_at: zBusinessDate,
};
export const offers = defineSyncedEntity(offersFields);
export type OfferLocal = z.infer<typeof offers.localEntity>;
export type OfferRemote = z.infer<typeof offers.remoteRow>;
export type OfferInsert = z.infer<typeof offers.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// statement_cycles
// ---------------------------------------------------------------------------

const statementCyclesFields = {
  id: zUuid,
  card_id: zUuid,
  cycle_start_date: zBusinessDate,
  cycle_end_date: zBusinessDate,
  statement_date: zBusinessDate.nullable(),
  payment_due_date: zBusinessDate.nullable(),
  is_projected: z.boolean(),
  statement_balance_cents: zCentsNull,
  minimum_payment_cents: zCentsNull,
  autopay_scheduled_cents: zCentsNull,
  autopay_scheduled_date: zBusinessDate.nullable(),
  paid_in_full_override: z.boolean().nullable(),
  paid_in_full_override_source_id: zUuid.nullable(),
  paid_in_full_override_note: panFreeStringNull,
  status: E.cycleStatus,
  reconciled_at: zTimestamptz.nullable(),
  source_id: zUuid.nullable(),
  last_verified_at: zBusinessDate.nullable(),
};
export const statementCycles = defineSyncedEntity(statementCyclesFields);
export type StatementCycleLocal = z.infer<typeof statementCycles.localEntity>;
export type StatementCycleRemote = z.infer<typeof statementCycles.remoteRow>;
export type StatementCycleInsert = z.infer<typeof statementCycles.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// purchase_queries
// ---------------------------------------------------------------------------

const purchaseQueriesFields = {
  id: zUuid,
  merchant_input: panFreeString,
  merchant_id: zUuid.nullable(),
  description: panFreeStringNull,
  category: E.spendCategory.nullable(),
  amount_cents: zCents,
  channel: E.txnChannel,
  geography: E.geoScope,
  currency: zCurrency,
  recurring: z.boolean(),
  owner: E.ownerType,
  financing_requested: z.boolean(),
  return_risk: z.boolean(),
  mode: E.priorityMode,
  pay_before_statement_close: z.boolean().nullable(),
  allow_interest_bearing: z.boolean(),
  entered_at: zTimestamptz,
};
export const purchaseQueries = defineSyncedEntity(purchaseQueriesFields);
export type PurchaseQueryLocal = z.infer<typeof purchaseQueries.localEntity>;
export type PurchaseQueryRemote = z.infer<typeof purchaseQueries.remoteRow>;
export type PurchaseQueryInsert = z.infer<typeof purchaseQueries.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// recommendation_results
// ---------------------------------------------------------------------------

const recommendationResultsFields = {
  id: zUuid,
  purchase_query_id: zUuid,
  rules_version: panFreeString,
  input_snapshot: zJson,
  input_fingerprint: panFreeString,
  recommended_card_id: zUuid.nullable(),
  alternative_card_id: zUuid.nullable(),
  no_eligible_card: z.boolean(),
  ranked: zJson,
  gates_failed: zJson,
  discounted_reward_cents: zCentsNull,
  discounted_offer_cents: zCentsNull,
  fx_fee_cents: zCentsNull,
  interest_risk_cents: zCentsNull,
  expected_net_cents: zCentsNull,
  utilization_after_bps: z.number().int().nullable(),
  confidence: E.confidenceTier,
  explanation: zStringArray,
  warnings: zJson,
  generated_at: zTimestamptz,
};
export const recommendationResults = defineSyncedEntity(recommendationResultsFields);
export type RecommendationResultLocal = z.infer<typeof recommendationResults.localEntity>;
export type RecommendationResultRemote = z.infer<typeof recommendationResults.remoteRow>;
export type RecommendationResultInsert = z.infer<typeof recommendationResults.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// payment_history_coverage
// ---------------------------------------------------------------------------

const paymentHistoryCoverageFields = {
  id: zUuid,
  card_id: zUuid,
  covers_from: zBusinessDate,
  covers_through: zBusinessDate,
  is_complete: z.boolean(),
  evidence_source_id: zUuid,
  observed_at: zTimestamptz,
  notes: panFreeStringNull,
};
export const paymentHistoryCoverage = defineSyncedEntity(paymentHistoryCoverageFields);
export type PaymentHistoryCoverageLocal = z.infer<typeof paymentHistoryCoverage.localEntity>;
export type PaymentHistoryCoverageRemote = z.infer<typeof paymentHistoryCoverage.remoteRow>;
export type PaymentHistoryCoverageInsert = z.infer<
  typeof paymentHistoryCoverage.remoteInsertPayload
>;

// ---------------------------------------------------------------------------
// transactions
// ---------------------------------------------------------------------------

const transactionsFields = {
  id: zUuid,
  card_id: zUuid,
  merchant_name_raw: panFreeString,
  merchant_id: zUuid.nullable(),
  description: panFreeStringNull,
  amount_cents: zPositiveCents,
  currency: zCurrency,
  original_amount_cents: zCentsNull,
  fx_rate_micros: zCentsNull,
  kind: E.txnType,
  transaction_date: zBusinessDate,
  posted_date: zBusinessDate.nullable(),
  status: E.txnStatus,
  category: E.spendCategory.nullable(),
  category_src: E.categorySource,
  owner: E.ownerType,
  channel: E.txnChannel,
  geography: E.geoScope,
  recurring_charge_id: zUuid.nullable(),
  statement_cycle_id: zUuid.nullable(),
  assignment: E.cycleAssignment,
  payment_state: E.txnPaymentStatus,
  expected_reward_rate_bps: z.number().int().nullable(),
  expected_reward_cents: zCentsNull,
  actual_reward_cents: zCentsNull,
  reward_rule_id: zUuid.nullable(),
  offer_id: zUuid.nullable(),
  recommendation_result_id: zUuid.nullable(),
  recommended_card_id: zUuid.nullable(),
  refunds_transaction_id: zUuid.nullable(),
  receipt_reference: panFreeStringNull,
  notes: panFreeStringNull,
};
export const transactions = defineSyncedEntity(transactionsFields);
export type TransactionLocal = z.infer<typeof transactions.localEntity>;
export type TransactionRemote = z.infer<typeof transactions.remoteRow>;
export type TransactionInsert = z.infer<typeof transactions.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// transaction_splits
// ---------------------------------------------------------------------------

const transactionSplitsFields = {
  id: zUuid,
  transaction_id: zUuid,
  amount_cents: zPositiveCents,
  category: E.spendCategory,
  reward_rule_id: zUuid.nullable(),
  expected_reward_cents: zCentsNull,
  notes: panFreeStringNull,
};
export const transactionSplits = defineSyncedEntity(transactionSplitsFields);
export type TransactionSplitLocal = z.infer<typeof transactionSplits.localEntity>;
export type TransactionSplitRemote = z.infer<typeof transactionSplits.remoteRow>;
export type TransactionSplitInsert = z.infer<typeof transactionSplits.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// card_payments
// ---------------------------------------------------------------------------

const cardPaymentsFields = {
  id: zUuid,
  card_id: zUuid,
  amount_cents: zPositiveCents,
  payment_date: zBusinessDate.nullable(),
  scheduled_date: zBusinessDate.nullable(),
  posted_date: zBusinessDate.nullable(),
  payment_source: panFreeStringNull,
  method: E.paymentMethod,
  status: E.paymentStatus,
  is_autopay: z.boolean(),
  confirmation_reference: panFreeStringNull,
  notes: panFreeStringNull,
};
export const cardPayments = defineSyncedEntity(cardPaymentsFields);
export type CardPaymentLocal = z.infer<typeof cardPayments.localEntity>;
export type CardPaymentRemote = z.infer<typeof cardPayments.remoteRow>;
export type CardPaymentInsert = z.infer<typeof cardPayments.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// payment_allocations
// ---------------------------------------------------------------------------

const paymentAllocationsFields = {
  id: zUuid,
  payment_id: zUuid,
  statement_cycle_id: zUuid.nullable(),
  transaction_id: zUuid.nullable(),
  amount_cents: zPositiveCents,
  basis: E.allocationBasis,
};
export const paymentAllocations = defineSyncedEntity(paymentAllocationsFields);
export type PaymentAllocationLocal = z.infer<typeof paymentAllocations.localEntity>;
export type PaymentAllocationRemote = z.infer<typeof paymentAllocations.remoteRow>;
export type PaymentAllocationInsert = z.infer<typeof paymentAllocations.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// cash_reservations
// ---------------------------------------------------------------------------

const cashReservationsFields = {
  id: zUuid,
  transaction_id: zUuid.nullable(),
  card_id: zUuid,
  amount_reserved_cents: zPositiveCents,
  checking_account_label: panFreeString,
  reservation_date: zBusinessDate,
  released_date: zBusinessDate.nullable(),
  status: E.reservationStatus,
};
export const cashReservations = defineSyncedEntity(cashReservationsFields);
export type CashReservationLocal = z.infer<typeof cashReservations.localEntity>;
export type CashReservationRemote = z.infer<typeof cashReservations.remoteRow>;
export type CashReservationInsert = z.infer<typeof cashReservations.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// deferred_interest_exposures
// ---------------------------------------------------------------------------

const deferredInterestExposuresFields = {
  id: zUuid,
  card_id: zUuid,
  original_amount_cents: zCents,
  remaining_cents: zCents,
  promo_end_date: zBusinessDate,
  apr_bps: z.number().int().nullable(),
  retroactive_risk_cents: zCentsNull,
  description: panFreeStringNull,
  evidence_source_id: zUuid,
};
export const deferredInterestExposures = defineSyncedEntity(deferredInterestExposuresFields);
export type DeferredInterestExposureLocal = z.infer<typeof deferredInterestExposures.localEntity>;
export type DeferredInterestExposureRemote = z.infer<typeof deferredInterestExposures.remoteRow>;
export type DeferredInterestExposureInsert = z.infer<
  typeof deferredInterestExposures.remoteInsertPayload
>;

// ---------------------------------------------------------------------------
// recurring_charges
// ---------------------------------------------------------------------------

const recurringChargesFields = {
  id: zUuid,
  merchant_id: zUuid.nullable(),
  merchant_name: panFreeString,
  expected_amount_cents: zCentsNull,
  frequency: panFreeString,
  assigned_card_id: zUuid.nullable(),
  next_expected_date: zBusinessDate.nullable(),
  category: E.spendCategory.nullable(),
  active: z.boolean(),
  last_transaction_id: zUuid.nullable(),
  reward_reason: panFreeStringNull,
  review_date: zBusinessDate.nullable(),
  variance_tolerance_bps: z.number().int(),
};
export const recurringCharges = defineSyncedEntity(recurringChargesFields);
export type RecurringChargeLocal = z.infer<typeof recurringCharges.localEntity>;
export type RecurringChargeRemote = z.infer<typeof recurringCharges.remoteRow>;
export type RecurringChargeInsert = z.infer<typeof recurringCharges.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// merchant_category_observations
// ---------------------------------------------------------------------------

const merchantCategoryObservationsFields = {
  id: zUuid,
  merchant_id: zUuid,
  card_id: zUuid,
  transaction_id: zUuid,
  channel: E.txnChannel,
  predicted_category: E.spendCategory.nullable(),
  predicted_rate_bps: z.number().int().nullable(),
  observed_reward_cents: zCentsNull,
  implied_rate_bps: z.number().int().nullable(),
  implied_category: E.spendCategory.nullable(),
  observed_at: zBusinessDate,
};
export const merchantCategoryObservations = defineSyncedEntity(merchantCategoryObservationsFields);
export type MerchantCategoryObservationLocal = z.infer<
  typeof merchantCategoryObservations.localEntity
>;
export type MerchantCategoryObservationRemote = z.infer<
  typeof merchantCategoryObservations.remoteRow
>;
export type MerchantCategoryObservationInsert = z.infer<
  typeof merchantCategoryObservations.remoteInsertPayload
>;

// ---------------------------------------------------------------------------
// review_items
// ---------------------------------------------------------------------------

const reviewItemsFields = {
  id: zUuid,
  card_id: zUuid.nullable(),
  kind: panFreeString,
  severity: reviewSeverity,
  affects_grace: z.boolean(),
  summary: panFreeString,
  detail: panFreeStringNull,
  unblocks: panFreeStringNull,
  resolved_at: zTimestamptz.nullable(),
};
export const reviewItems = defineSyncedEntity(reviewItemsFields);
export type ReviewItemLocal = z.infer<typeof reviewItems.localEntity>;
export type ReviewItemRemote = z.infer<typeof reviewItems.remoteRow>;
export type ReviewItemInsert = z.infer<typeof reviewItems.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// import_batches
// ---------------------------------------------------------------------------

const importBatchesFields = {
  id: zUuid,
  label: panFreeString,
  transform_version: panFreeString,
  input_bundle_hash: panFreeString,
  manifest_hashes: zJson,
  rows_written: zJson,
  values_skipped: z.number().int(),
  review_items_created: z.number().int(),
  supersedes_batch_id: zUuid.nullable(),
  notes: panFreeStringNull,
};
export const importBatches = defineSyncedEntity(importBatchesFields);
export type ImportBatchLocal = z.infer<typeof importBatches.localEntity>;
export type ImportBatchRemote = z.infer<typeof importBatches.remoteRow>;
export type ImportBatchInsert = z.infer<typeof importBatches.remoteInsertPayload>;

// ---------------------------------------------------------------------------
// settings
// ---------------------------------------------------------------------------

const settingsFields = {
  id: zUuid,
  utilization_preferred_bps: z.number().int(),
  utilization_warning_bps: z.number().int(),
  utilization_hard_bps: z.number().int(),
  overall_utilization_target_bps: z.number().int(),
  default_mode: E.priorityMode,
  assume_pay_before_close: z.boolean(),
  require_active_grace_for_spending: z.boolean(),
  allow_interest_bearing_recommendations: z.boolean(),
  treat_likely_active_as_eligible: z.boolean(),
  min_confidence_to_recommend: E.confidenceTier,
  assumed_worst_apr_bps: z.number().int(),
  protection_prompt_threshold_cents: zCents,
  financial_staleness_days: z.number().int(),
  autopay_staleness_days: z.number().int(),
  grace_confirmation_ttl_days: z.number().int(),
  staleness_days: zJson,
  checking_balance_cents: zCentsNull,
  checking_balance_updated_at: zBusinessDate.nullable(),
  rules_version: panFreeString,
  schema_version: z.number().int(),
};
export const settings = defineSyncedEntity(settingsFields);
export type SettingsLocal = z.infer<typeof settings.localEntity>;
export type SettingsRemote = z.infer<typeof settings.remoteRow>;
export type SettingsInsert = z.infer<typeof settings.remoteInsertPayload>;
